import { X, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { spacing } from '../../utils/spacing';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary';
  loading?: boolean;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
}: ConfirmModalProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <div className="flex items-start gap-4 mb-4">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: confirmVariant === 'danger' 
                ? 'rgb(var(--color-destructive-light))' 
                : 'rgb(var(--color-primary-light))',
              borderRadius: 'var(--radius-full)',
            }}
          >
            <AlertTriangle
              className="w-5 h-5"
              style={{
                color: confirmVariant === 'danger'
                  ? 'rgb(var(--color-destructive))'
                  : 'rgb(var(--color-primary))',
              }}
            />
          </div>
          <div className="flex-1">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="mt-2">{message}</DialogDescription>
          </div>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: 'rgb(var(--color-text-tertiary))',
              transition: 'var(--transition-base)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'rgb(var(--color-text))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgb(var(--color-text-tertiary))';
            }}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            size="md"
          >
            {cancelText}
          </Button>
          <Button
            variant={confirmVariant === 'danger' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={loading}
            size="md"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
